/**
 * The pricing card grid, shared by the landing section and the /pricing page.
 *
 * Both previously kept their own near-identical copy of this markup AND their own
 * hardcoded plan array, which is how the site ended up advertising prices the
 * backend never enforced. Presentation only — data and navigation come from props.
 */
import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { Card } from "components/ui/card";
import { Button } from "components/ui/button";
import { Plan, formatPrice, planBullets, planCtaLabel } from "../../config/plans";

interface PricingPlansProps {
  plans: Plan[];
  onSelect: (plan: Plan) => void;
  /**
   * "section" animates on scroll (landing page, below the fold); "page" animates
   * immediately (/pricing, above the fold — whileInView there would leave the
   * cards invisible until a scroll that may never happen).
   */
  variant?: "section" | "page";
}

const SIZES = {
  section: { card: "p-6", price: "text-4xl" },
  page: { card: "p-8", price: "text-5xl" },
} as const;

const PricingPlans: React.FC<PricingPlansProps> = ({ plans, onSelect, variant = "section" }) => {
  const sizes = SIZES[variant];
  const reveal =
    variant === "page"
      ? { animate: { opacity: 1, y: 0 } }
      : { whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
      {plans.map((plan, index) => {
        const price = formatPrice(plan);
        return (
          <motion.div
            key={plan.id}
            initial={{ opacity: 0, y: 20 }}
            {...reveal}
            transition={{ duration: 0.5, delay: index * 0.08 }}
          >
            <Card
              className={`${sizes.card} h-full flex flex-col relative ${
                plan.popular ? "border-primary shadow-glow" : ""
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap">
                  Most Popular
                </div>
              )}

              <h3 className="text-xl font-heading font-bold mb-1">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{plan.tagline}</p>

              <div className="mb-6">
                <span className={`${sizes.price} font-bold`}>{price.amount}</span>
                {price.suffix && (
                  <span className="text-muted-foreground text-sm ml-1">{price.suffix}</span>
                )}
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {planBullets(plan).map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => onSelect(plan)}
                aria-label={planCtaLabel(plan)}
                className={plan.popular ? "bg-gradient-primary hover:shadow-glow" : ""}
                variant={plan.popular ? "default" : "outline"}
                size="lg"
              >
                {planCtaLabel(plan)}
              </Button>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
};

export default PricingPlans;
